import { useMemo, useEffect, useRef } from 'react';
import { isSprintCreatePending } from '../../../utils/pendingSprintCreates';
import { DEFAULT_STATUSES } from '../FlowchartView/TaskNode/constants';
import { 
  calculateProgress, 
  findPreferredStatus, 
  isStatusCompleted,
  isStatusBlocked,
  isStatusInProgress,
} from '../FlowchartView/utils/statusUtils';
import { categoryForNestedCampaign, deriveCampaignActivity } from '../shared/utils/taskAvailabilityUtils';

/**
 * useProgressSync Hook
 * 
 * Handles progress calculation and auto-syncing task status based on nested sprint progress.
 * 
 * Features:
 * - Calculates overall progress from tasks and nested sprints (RECURSIVELY)
 * - Auto-updates parent task status when nested sprint progress changes
 * - Syncs calculated progress to sprint.progress field
 * - Syncs each child nested campaign's stored progress (for flow/table nested bars and APIs)
 * - Saves activity (not started / in progress / blocked / done) on the open campaign
 *   so a parent can inherit it from the list without loading missions
 */
export const useProgressSync = ({
  tasks,
  statuses,
  localSprint,
  setLocalSprint,
  nestedSprints,
  allSprints = [],
  onUpdateSprint,
  isMultiCampaignMode = false,
  isTemplate = false,
}) => {
  // Calculate progress (using category-based completion, with RECURSIVE nested sprint support)
  const calculatedProgress = useMemo(() => {
    return calculateProgress(tasks, statuses, nestedSprints, allSprints);
  }, [tasks, statuses, nestedSprints, allSprints]);

  // Same inputs as progress. Null when a nested campaign is still an unlabeled shell.
  const campaignActivity = useMemo(() => {
    if (!Array.isArray(tasks)) return null;
    return deriveCampaignActivity(tasks, statuses, nestedSprints, allSprints);
  }, [tasks, statuses, nestedSprints, allSprints]);

  // Sync calculated progress and activity onto the open campaign.
  // Templates are blueprints — skip to avoid pointless saves on open/create.
  // NOTE: We exclude localSprint from dependencies to avoid infinite loops.
  useEffect(() => {
    if (isTemplate) return;
    setLocalSprint(prev => {
      if (!prev) return prev;
      const activityUnchanged = campaignActivity == null || prev.activity === campaignActivity;
      if (prev.progress === calculatedProgress && activityUnchanged) return prev;
      const next = { ...prev, progress: calculatedProgress };
      if (campaignActivity != null) next.activity = campaignActivity;
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedProgress, campaignActivity, setLocalSprint, isTemplate]);

  // Keep child nested campaigns' sprint.progress aligned with their missions (same rules as calculateProgress)
  // Templates are blueprints — skip persisting progress to avoid save storms on nested children.
  useEffect(() => {
    if (!onUpdateSprint || isMultiCampaignMode || isTemplate) return;
    const sid = localSprint?.id;
    if (!sid || String(sid).startsWith('view-')) return;
    for (const ns of nestedSprints) {
      if (!ns?.id || isSprintCreatePending(ns.id)) continue;
      const nestedTasks = ns.tasks;
      const nestedStatuses = ns.statuses || DEFAULT_STATUSES;
      const grandchildSprints = allSprints.filter((s) => s.parentSprintId === ns.id);
      let computed;
      if (nestedTasks && nestedTasks.length > 0) {
        computed = calculateProgress(nestedTasks, nestedStatuses, grandchildSprints, allSprints);
      } else if (Array.isArray(nestedTasks) && nestedTasks.length === 0) {
        computed = 0;
      } else {
        continue;
      }
      const activity = deriveCampaignActivity(nestedTasks, nestedStatuses, grandchildSprints, allSprints);
      const progressChanged = (ns.progress || 0) !== computed;
      const activityChanged = activity != null && ns.activity !== activity;
      if (progressChanged || activityChanged) {
        onUpdateSprint({
          ...ns,
          progress: computed,
          ...(activity != null ? { activity } : {}),
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nestedSprints, allSprints, onUpdateSprint, localSprint?.id, isMultiCampaignMode, isTemplate]);

  // Track which tasks we've already synced to prevent re-running on our own updates
  const lastSyncedRef = useRef(new Map()); // Map<taskId, { status, progress }>
  
  // Auto-sync task status based on nested sprint progress
  // When a nested sprint goes from 0% → 1%+, OR any task inside is "in progress", set parent task to "In Progress"
  // When a nested sprint hits 100%, set parent task to "Completed"
  // When started but not complete and no inner missions are actionable, set parent task to "Blocked"
  // 
  // NOTE: We intentionally exclude localSprint from dependencies to avoid infinite loops.
  // The effect uses functional updates (prev => ...) so it doesn't need localSprint directly.
  // It only needs to run when nestedSprints change (which indicates actual progress changes).
  useEffect(() => {
    if (nestedSprints.length === 0 || isTemplate) return;

    const tasksWithNestedSprints = tasks.filter(t => t.nestedSprintId);
    if (tasksWithNestedSprints.length === 0) return;
    
    let needsUpdate = false;
    const taskUpdates = {};
    
    for (const task of tasksWithNestedSprints) {
      // Skip tasks with manual status override (statusAutoSync explicitly set to false)
      if (task.statusAutoSync === false) continue;
      
      const nestedSprint = nestedSprints.find(ns => ns.id === task.nestedSprintId);
      if (!nestedSprint) continue;

      const nestedTasksRaw = nestedSprint.tasks;
      const nestedStatuses = nestedSprint.statuses || DEFAULT_STATUSES;
      const grandchildSprints = allSprints.filter((s) => s.parentSprintId === nestedSprint.id);
      let progress;
      if (nestedTasksRaw && nestedTasksRaw.length > 0) {
        progress = calculateProgress(nestedTasksRaw, nestedStatuses, grandchildSprints, allSprints);
      } else if (Array.isArray(nestedTasksRaw) && nestedTasksRaw.length === 0) {
        progress = 0;
      } else {
        progress = nestedSprint.progress || 0;
      }
      
      const currentCategory = isStatusCompleted(task.status, statuses) ? 'completed'
        : isStatusBlocked(task.status, statuses) ? 'blocked'
        : isStatusInProgress(task.status, statuses) ? 'in_progress'
        : 'not_started';

      // Unlabeled shells stay as they are. A saved activity, or loaded missions, decide the rest.
      const desiredCategory = categoryForNestedCampaign(task, nestedSprints, statuses, allSprints);
      if (!desiredCategory) continue;

      const lastSynced = lastSyncedRef.current.get(task.id);
      if (lastSynced && lastSynced.progress === progress && lastSynced.desiredCategory === desiredCategory) {
        continue;
      }

      if (desiredCategory !== currentCategory) {
        const preferredStatus = findPreferredStatus(statuses, desiredCategory);
        if (preferredStatus) {
          const updates = { status: preferredStatus.id };
          if (desiredCategory === 'completed') {
            updates.completedAt = new Date().toISOString();
          } else if (currentCategory === 'completed') {
            updates.completedAt = null;
          }
          taskUpdates[task.id] = updates;
          lastSyncedRef.current.set(task.id, { progress, status: preferredStatus.id, desiredCategory });
          needsUpdate = true;
        }
      } else {
        lastSyncedRef.current.set(task.id, { progress, status: task.status, desiredCategory });
      }
    }
    
    if (needsUpdate) {
      setLocalSprint(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map(t => 
            taskUpdates[t.id] ? { ...t, ...taskUpdates[t.id] } : t
          ),
        };
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nestedSprints, statuses, allSprints, setLocalSprint, isTemplate]); // Removed 'tasks' - only run when nested sprints or statuses change

  return { calculatedProgress };
};

export default useProgressSync;

